import {
  Component,
  signal,
  inject,
  input,
  effect,
  output,
  computed,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';

// Angular Material Imports
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';

// Lucide Icons
import { LucideAngularModule, Info } from 'lucide-angular';
import { ItemSelectorComponent } from './item-selector/item-selector.component';
import { Subscription, firstValueFrom } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { AddListDialog } from './add-list-dialog/add-list-dialog';
import { ListOptionManager } from './list-option-manager/list-option-manager';
import { SurveyListStore } from '../base-classes/survey-list-controller.state';
import { SurveyListOptionService } from '../survey-list-option/survey-list-option';

@Component({
  selector: 'lib-list-manager',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    MatIconModule,
    LucideAngularModule,
    ItemSelectorComponent,
    MatDialogModule,
    ListOptionManager,
  ],
  templateUrl: './list-manager.html',
  styleUrl: './list-manager.scss',
})
export class ListManagerComponent {
  readonly store = inject(SurveyListStore);
  private route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);
  es_entityName = input.required<string>();
  elementId = input.required<string | undefined>();
  surveyId = signal<number | undefined>(undefined);
  readonly InfoIcon = Info;

  listSet = output<string | undefined>();
  private listOptionService = inject(SurveyListOptionService);
  private subscriptions = new Subscription();
  constructor() {
    effect(() => {
      this.store.setActiveList(this.elementId());
    });

    effect(() => {
      const activeListId = this.store.activeListId();
      this.listSet.emit(activeListId);
    });

    this.route.parent?.params.subscribe((params) => {
      const id = params['id'];
      this.surveyId.set(id ? Number(id) : undefined);
      if (id) {
        this.store.loadLists(id);
      } else {
        this.store.reset();
      }
    });
  }

  listName = computed(() => {
    const lists = this.store.lists();
    const nextNumber = (lists?.length || 0) + 1;
    return `List ${nextNumber}`;
  });
  async openListCreateModal() {
    if (this.store.creatingList()) {
      return;
    }
    const listName = this.listName();
    const dialogRef = this.dialog.open(AddListDialog, {
      width: '500px',
      maxWidth: '95vw',
      disableClose: false,
      data: {
        surveyId: this.surveyId(),
        es_entityName: this.es_entityName(),
        elementId: this.elementId(),
        listName: listName,
      },
    });

    const result = await firstValueFrom(dialogRef.afterClosed());

    if (result?.success) {
      await this.store.createListAndUpdateEntity(result.data).then((d) => {
        this.changeList(d?.listId);
        this.listSet.emit(d?.listId);
      });
    }
  }

  changeList(listId: string) {
    this.store.setActiveList(listId);
  }
  @ViewChild(ListOptionManager) listOptionManager!: ListOptionManager;
  ngOnInit() {
    this.subscriptions.add(
      this.listOptionService.listUpdated$.subscribe((listId) => {
        const currentListId = this.store.activeListId();
        // If the updated list is the currently active list, refresh the options
        if (currentListId === listId && this.listOptionManager) {
          this.listOptionManager.reloadItems(listId);
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
